import { BookOpen, Globe, Plus, Users, X } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import UserSearch from '@/components/chat/user-search'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

import { Role } from '@/enums/role.enum'
import courseService from '@/services/course.service'

import { ConversationType } from '@/types/chat.type'
import type { Course } from '@/types/course-detail.type'
import type { UserProfile } from '@/types/user.type'

interface CreateGroupChatDialogProps {
  isOpen: boolean
  onClose: () => void
  onCreateGroupChat: (
    name: string,
    description: string,
    participantIds: number[],
    type?: ConversationType,
    addAllUsers?: boolean,
    courseIds?: number[]
  ) => Promise<void>
  authUser: UserProfile | null
  isCreating: boolean
}

interface SelectedUser {
  id: number
  firstName: string
  lastName: string
  email: string
  avatar?: string
}

export const CreateGroupChatDialog: React.FC<CreateGroupChatDialogProps> = ({
  isOpen,
  onClose,
  onCreateGroupChat,
  authUser,
  isCreating,
}) => {
  const [groupName, setGroupName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [selectedUsers, setSelectedUsers] = React.useState<SelectedUser[]>([])
  const [groupType, setGroupType] = React.useState<ConversationType>(ConversationType.GROUP)
  const [showSelectedUsers, setShowSelectedUsers] = React.useState(false)
  const [addByCourse, setAddByCourse] = React.useState(false)
  const [selectedCourses, setSelectedCourses] = React.useState<Course[]>([])
  const [courses, setCourses] = React.useState<Course[]>([])
  const [isLoadingCourses, setIsLoadingCourses] = React.useState(false)
  const [showCoursesPopup, setShowCoursesPopup] = React.useState(false)

  // Check if user is admin
  const isAdmin = authUser?.roles?.includes(Role.ADMIN) ?? false

  // Load courses
  const loadCourses = React.useCallback(async () => {
    setIsLoadingCourses(true)
    try {
      const coursesData = await courseService.getListAllCourses<Course[]>()
      setCourses(coursesData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load courses:', error)
    } finally {
      setIsLoadingCourses(false)
    }
  }, [])

  // Load courses when dialog opens
  React.useEffect(() => {
    if (isOpen && isAdmin) {
      loadCourses()
    }
  }, [isOpen, isAdmin, loadCourses])

  // Handle course selection (add/remove from selected courses)
  const handleCourseSelect = React.useCallback((course: Course) => {
    setSelectedCourses(prev => {
      const isSelected = prev.some(c => c.id === course.id)

      if (isSelected) {
        // Remove course if already selected
        return prev.filter(c => c.id !== course.id)
      } else {
        // Add course if not selected
        return [...prev, course]
      }
    })

    // Clear selected users when switching to course mode
    setSelectedUsers([])
  }, [])

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setGroupName('')
      setDescription('')
      setSelectedUsers([])
      setGroupType(ConversationType.GROUP)
      setShowSelectedUsers(false)
      setAddByCourse(false)
      setSelectedCourses([])
      setShowCoursesPopup(false)
    }
  }, [isOpen])

  const handleCreateGroupChat = async () => {
    if (!groupName.trim()) {
      return
    }

    // Validate that we have participants
    const hasParticipants =
      groupType === ConversationType.COMMUNITY ||
      selectedUsers.length > 0 ||
      (addByCourse && selectedCourses.length > 0)
    if (!hasParticipants) {
      return
    }

    // Validate admin permission for private groups and communities
    if ((groupType === ConversationType.PRIVATE_GROUP || groupType === ConversationType.COMMUNITY) && !isAdmin) {
      return
    }

    try {
      let participantIds: number[] = []
      let courseIds: number[] | undefined = undefined

      if (groupType === ConversationType.COMMUNITY) {
        // Community automatically adds all users
        participantIds = []
      } else if (addByCourse && selectedCourses.length > 0) {
        // Course users will be handled by the parent component
        participantIds = []
        courseIds = selectedCourses.map(course => course.id)
      } else {
        // Add selected individual users
        participantIds = selectedUsers.map(u => u.id)
      }

      await onCreateGroupChat(groupName.trim(), description.trim(), participantIds, groupType, false, courseIds)
      onClose()
    } catch (error) {
      // Error handling is done in the parent component
      // eslint-disable-next-line no-console
      console.error('Failed to create group chat:', error)
    }
  }

  const handleUserSelect = (user: SelectedUser) => {
    setSelectedUsers(prev => [...prev, user])
  }

  const handleUserRemove = (userId: number) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
  }

  const isFormValid =
    groupName.trim() &&
    (groupType === ConversationType.COMMUNITY ||
      selectedUsers.length > 0 ||
      (addByCourse && selectedCourses.length > 0))

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Create Group Chat</h3>
                <p className="text-sm text-gray-500">Start a conversation with multiple people</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Group Name */}
            <div>
              <Label htmlFor="group-name" className="text-sm font-medium text-gray-700">
                Group Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="group-name"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                placeholder="Enter group name"
                className="mt-1"
                maxLength={50}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter group description"
                className="mt-1"
                rows={3}
                maxLength={200}
              />
            </div>

            {/* Group Type */}
            {isAdmin && (
              <div>
                <Label htmlFor="group-type" className="text-sm font-medium text-gray-700">
                  Group Type <span className="text-red-500">*</span>
                </Label>
                <Select value={groupType} onValueChange={value => setGroupType(value as ConversationType)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select group type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ConversationType.GROUP}>Group</SelectItem>
                    <SelectItem value={ConversationType.PRIVATE_GROUP}>Private Group</SelectItem>
                    <SelectItem value={ConversationType.COMMUNITY}>Community</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Add Participants Options */}
            {groupType !== ConversationType.COMMUNITY && (
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Add Participants <span className="text-red-500">*</span>
                </Label>
                <div className="mt-2 space-y-4">
                  {/* Radio Options - Show for Private Groups only */}
                  {isAdmin && groupType === ConversationType.PRIVATE_GROUP && (
                    <RadioGroup
                      value={addByCourse ? 'by-course' : 'individual'}
                      onValueChange={value => {
                        const isAddByCourse = value === 'by-course'

                        setAddByCourse(isAddByCourse)

                        if (isAddByCourse) {
                          setSelectedUsers([]) // Clear selected users when adding by course
                        } else {
                          setSelectedCourses([]) // Clear selected courses when not adding by course
                        }
                      }}
                      className="space-y-3"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="individual" id="individual" />
                        <Label htmlFor="individual" className="text-sm font-medium text-gray-700 cursor-pointer">
                          Add Individually
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="by-course" id="by-course" />
                        <Label htmlFor="by-course" className="text-sm font-medium text-gray-700 cursor-pointer">
                          Add by Courses
                        </Label>
                      </div>
                    </RadioGroup>
                  )}
                  {/* User Search - Show for regular groups or when adding individual users in private groups */}
                  {(!isAdmin || groupType !== ConversationType.PRIVATE_GROUP || !addByCourse) && (
                    <div className="mt-3">
                      <UserSearch
                        onUserSelect={handleUserSelect}
                        placeholder="Search users to add..."
                        excludeUserIds={authUser?.id ? [authUser.id] : []}
                        maxUsers={20}
                      />
                    </div>
                  )}

                  {/* Course Selection - Show when adding by course */}
                  {addByCourse && (
                    <div className="mt-3">
                      <Label className="text-sm font-medium text-gray-700">
                        Select Courses <span className="text-red-500">*</span>
                      </Label>
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowCoursesPopup(true)}
                          className="w-full justify-between"
                          disabled={isLoadingCourses}
                        >
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            <span>
                              {isLoadingCourses
                                ? 'Loading courses...'
                                : selectedCourses.length === 0
                                  ? 'Select courses to add users from'
                                  : `${selectedCourses.length} course${selectedCourses.length === 1 ? '' : 's'} selected`}
                            </span>
                          </div>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Community Info Message */}
            {groupType === ConversationType.COMMUNITY && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Community Chat</span>
                </div>
                <p className="text-sm text-blue-700 mt-2">
                  All users in the system will automatically be added to this community chat. This allows for open
                  discussion and collaboration across the entire platform.
                </p>
              </div>
            )}

            {/* Selected Users Button */}
            {selectedUsers.length > 0 && (
              <div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSelectedUsers(true)}
                  className="w-full justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>Selected Participants ({selectedUsers.length})</span>
                  </div>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={onClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroupChat}
              disabled={!isFormValid || isCreating}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isCreating ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Create Group Chat
                </div>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Selected Users Popup */}
      {showSelectedUsers && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSelectedUsers(false)} />
          <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Selected Participants</h3>
                    <p className="text-sm text-gray-500">{selectedUsers.length} users selected</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowSelectedUsers(false)} className="p-1">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Users List */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {selectedUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                      {user.avatar ? (
                        <Image
                          src={user.avatar}
                          alt={`${user.firstName} ${user.lastName}`}
                          width={40}
                          height={40}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-600">
                          {user.firstName.charAt(0)}
                          {user.lastName.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUserRemove(user.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={() => setShowSelectedUsers(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Courses Selection Popup */}
      {showCoursesPopup && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCoursesPopup(false)} />
          <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Select Courses</h3>
                    <p className="text-sm text-gray-500">Choose courses to add users from</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowCoursesPopup(false)} className="p-1">
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Select All Button */}
              {!isLoadingCourses && courses.length > 0 && (
                <div className="mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const allSelected = selectedCourses.length === courses.length
                      if (allSelected) {
                        setSelectedCourses([])
                      } else {
                        setSelectedCourses([...courses])
                      }
                    }}
                    className="w-full"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        {selectedCourses.length === courses.length ? 'Deselect All' : 'Select All'} ({courses.length}{' '}
                        courses)
                      </span>
                    </div>
                  </Button>
                </div>
              )}

              {/* Courses List */}
              {isLoadingCourses ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                  <p>Loading courses...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {courses.map(course => {
                    const isSelected = selectedCourses.some(c => c.id === course.id)
                    return (
                      <div
                        key={course.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border border-blue-200'
                            : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => handleCourseSelect(course)}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <BookOpen className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm text-gray-900">{course.title}</div>
                            {course.content && (
                              <div className="text-xs text-gray-500 mt-1">{course.content.substring(0, 120)}...</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <Button variant="outline" onClick={() => setShowCoursesPopup(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
